import styled from '@emotion/styled';
import * as React from 'react';

import { Button, PresetThemes } from '@travelnest/merlin.button';
import { IconButton } from '@travelnest/merlin.button-icon';
import { Rule } from '@travelnest/merlin.rule';
import theme from '@travelnest/merlin.theme';
import { Heading } from '@travelnest/merlin.typography';

import { usePrevious } from '@travelnest/merlin-hooks';
import { InlineError } from '@travelnest/merlin.inline-message';
import { useActionCallback, useActionErrorNotifier } from '../../../hooks';
import { ICalendarEvent } from '../../components/calendar/types';
import { BreakpointType } from './Calendar';
import { dateDiff, DateRange, datesToRanges } from './calendar-helpers';
import { CustomPriceInput } from './CustomPriceInput';
import { EventSelectionStatus, EventStatus } from './EventSelectionStatus';
import { SelectionSummary } from './SelectionSummary';

import { IPricingOverride } from '../../../data/entities/pricing';
import { RadioQuestion } from '../../../view/components/radio/RadioQuestion';

import { Spacer } from '@travelnest/merlin.spacer';

const StyledContextPanel = styled.div`
	@media only screen and (max-width: 1023px) {
		position: fixed;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background: #fff;
		z-index: 2147483002; /* Above Intercom at 2147483001 */
		overflow-y: scroll;
	}
`;

const StyledButtonDiv = styled.div`
	button {
		&:nth-of-type(1){
			margin-right: ${theme.layout.spacing.standard};
	}
}`;

const StyledPaddingWrapper = styled.div`
	padding: 0 ${theme.layout.spacing.large} ${theme.layout.spacing.large} ${theme.layout.spacing.large};
`;

const HeaderWrapper = styled.div`
	display: flex;
	padding: 15px;
	height: 70px;
	vertical-align: center;
	align-items: center;

	button {
		&:nth-of-type(1){
			margin-right: ${theme.layout.spacing.large};
			width: 40px;
			padding: 5px;
		}
	}
`;

interface ICalendarContextProps {
	selectedDates: Date[];
	selectedEvents: ICalendarEvent[];
	selectedAvailableDates: Date[];
	breakpoint: BreakpointType;
	propertyId: string;

	clearSelection: () => any;
	onSelectionChange: (dates: Date[]) => void;
	onConfirm: (blockDates: boolean, ranges: DateRange[], isOverride: boolean, override: IPricingOverride | {}) => void;
	onConfirmLoading: boolean;
	onCancel: () => void;
	onBack: () => any;
	onSuccessfulRequest: () => void;

	pricingType?: string;
	currency?: string;
}

export const ContextualActions: React.FC<ICalendarContextProps> = (props) => {
	const {
		selectedDates,
		selectedEvents,
		selectedAvailableDates,
		breakpoint,
		onConfirm,
		onConfirmLoading,
		onCancel,
		onBack,
		propertyId,
		onSuccessfulRequest,
		pricingType,
		currency
	} = props;

	const ranges = datesToRanges(selectedDates.sort((a, b) => a > b ? 1 : -1));
	const nights = ranges.reduce((sum, selection) => sum + dateDiff(...selection) + 1, 0);

	const selectedEventStatuses = [
		...new Set(selectedEvents?.map((e) => e?.status)),
		selectedAvailableDates.length > 0 ? 'available' : undefined,
	].filter((a) => !!a) as EventStatus[];
	const selectedBookingIds = selectedEvents?.filter((e) => e?.status === 'booked').map((e) => e.id) || [];
	const canModify = selectedDates.length > 0 && !(selectedEventStatuses.length === 1 && selectedEventStatuses[0] === 'booked');

	const isSmartPricing = pricingType === 'automatic';
	const [block, setBlock] = React.useState<boolean>(false);
	const [isCustomPrice, setIsCustomPrice] = React.useState<boolean>(false);
	const [customPriceValue, setCustomPriceValue] = React.useState<number | undefined>(undefined);
	const previousSelectEventStatuses = usePrevious(selectedEventStatuses);
	React.useEffect(() => {
		if (breakpoint === 'mobile') {
			// run the same effect as below on mobile devices on mount of component only
			// we need to do this because usePrevious ends up always undefined due to unmounting
			if (selectedEventStatuses.length === 1 && selectedEventStatuses[0] === 'available') {
				setBlock(false);
			} else {
				setBlock(true);
			}
		}
	}, []);
	React.useEffect(() => {
		if (previousSelectEventStatuses && previousSelectEventStatuses.length !== selectedEventStatuses.length) {
			// if the list of event status types changes then we need to recalculate the default value of the radio group
			if (selectedEventStatuses.length === 1 && selectedEventStatuses[0] === 'available') {
				setBlock(false);
			} else {
				setBlock(true);
			}
		}
	}, [selectedEventStatuses]);
	// if block state changes, reset custom price state to defaults
	React.useEffect(() => {
		setIsCustomPrice(false);
		setCustomPriceValue(undefined);
	}, [block]);

	const [showValidation, setShowValidation] = React.useState(true);
	const resolveEventError = useActionErrorNotifier('RESOLVE_EVENTS_REQUEST', 'RESOLVE_EVENTS_FAILURE');
	const deleteEventError = useActionErrorNotifier('AVAILABILITY_EVENT_DELETE_REQUEST', 'AVAILABILITY_EVENT_DELETE_FAILURE');

	React.useEffect(() => {
		if ((resolveEventError || deleteEventError) && !showValidation) {
			setShowValidation(true);
		}
	}, [resolveEventError, deleteEventError]);

	const onActionSuccess = () => {
		onSuccessfulRequest();
		setShowValidation(false);
	};
	useActionCallback(onActionSuccess, 'RESOLVE_EVENTS_RECEIVE');
	useActionCallback(onActionSuccess, 'AVAILABILITY_EVENT_DELETE_RECEIVE');

	const headingText = isSmartPricing ? 'Update nights' : 'Block or unblock nights';

	return <StyledContextPanel>
		{breakpoint === 'mobile' &&
			<HeaderWrapper>
				<IconButton
					icon="left"
					onClick={(e) => {
						e.preventDefault();
						onBack();
					}}
					iconPosition="center"
				/>
				<Heading>{headingText}</Heading>
			</HeaderWrapper>
		}
		<SelectionSummary nightsOnly={true} nights={nights} breakpoint={breakpoint} inContextPanel={true} dateRanges={ranges} />
		<EventSelectionStatus
			selectedBookingIds={selectedBookingIds}
			selectedEventStatuses={selectedEventStatuses}
			nights={nights}
			propertyId={propertyId}
		/>
		{nights > 0 &&
			<StyledPaddingWrapper>
				<Rule />
				{canModify &&
					<>
						<div data-testid="context-blocker">
							<RadioQuestion
								id={'blocked-choice'}
								question={'Show property as'}
								options={{
									'Available': false,
									'Blocked': true
								}}
								selected={block}
								onChange={(_, value) => setBlock(value)}
								horizontalAlignment
							/>
						</div>
						{isSmartPricing && !block &&
							<>
								<div data-testid="context-smartprice-or-custom">
									<RadioQuestion
										id={'smartprice-or-custom-choice'}
										question={'Set price as'}
										options={{
											'Smart price': false,
											'Custom': true
										}}
										selected={isCustomPrice}
										onChange={(_, value) => setIsCustomPrice(value)}
										horizontalAlignment
									/>
								</div>
							</>
						}
						{isCustomPrice && !block &&
							<>
								<div data-testid="context-custom-price-input">
									<CustomPriceInput
										id="custom-price-input"
										value={customPriceValue}
										handleChange={(e: number) => setCustomPriceValue(e)}
										label="New nightly price"
										nights={nights}
										currency={currency} />
								</div>
							</>
						}
					</>
				}
				<StyledButtonDiv >
					{canModify &&
						<Button
							buttonType={PresetThemes.Primary}
							data-testid="context-confirm"
							onClick={(e) => {
								e.preventDefault(); onConfirm(
									block,
									ranges,
									isCustomPrice,
									customPriceValue ? { price: customPriceValue } : {});
							}}
							loading={onConfirmLoading}
							disabled={isCustomPrice && !customPriceValue}
						>
							Confirm
						</Button>
					}
					<Button data-testid="context-cancel" onClick={(e) => { e.preventDefault(); onCancel(); setShowValidation(false); }}>Cancel</Button>
				</StyledButtonDiv>
				{resolveEventError && showValidation &&
					<>
						<Spacer />
						<InlineError text="Something went wrong. We couldn't save your changes. Please try again later." />
					</>}
			</StyledPaddingWrapper>
		}
	</StyledContextPanel>;
};
